import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { plansAPI } from '../services/api';
import { countriesAPI, currenciesAPI } from '../services/mastersAPI';
import Layout from '../components/layout/Layout';
import LoadingSpinner from '../components/common/LoadingSpinner';
import toast from 'react-hot-toast';
import {
  PlusIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
  CurrencyDollarIcon,
  ServerIcon,
  UsersIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClipboardDocumentListIcon,
  GlobeAltIcon,
  ChartBarIcon,
  SparklesIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

const Plans = () => {
  const [plans, setPlans] = useState([]);
  const [countries, setCountries] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('view'); // 'view', 'create', 'edit'
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [planToDelete, setPlanToDelete] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    discount: 0,
    country: '',
    currency: '',
    billingCycle: 'monthly',
    storageLimitMB: '',
    maxUsers: '',
    enabledModules: {
      leads: true,
      customers: true,
      followups: true,
      analytics: false,
      reports: false,
      integrations: false
    },
    features: [],
    isActive: true
  });

  useEffect(() => {
    fetchPlans();
    fetchCountries();
    fetchCurrencies();
  }, []);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const response = await plansAPI.getAll();
      setPlans(response.data.plans || []);
    } catch (error) {
      console.error('Error fetching plans:', error);
      toast.error('Failed to load plans');
    } finally {
      setLoading(false);
    }
  };

  const fetchCountries = async () => {
    try {
      const response = await countriesAPI.getAllCountries();
      setCountries(response);
    } catch (error) {
      console.error('Error fetching countries:', error);
      toast.error('Failed to load countries');
    }
  };

  const fetchCurrencies = async () => {
    try {
      const response = await currenciesAPI.getAllCurrencies();
      setCurrencies(response);
    } catch (error) {
      console.error('Error fetching currencies:', error);
      toast.error('Failed to load currencies');
    }
  };

  const handleCountryChange = (countryId) => {
    const selectedCountry = countries.find(country => country._id === countryId);
    if (selectedCountry) {
      // Auto-select currency based on country
      const countryCurrency = currencies.find(currency => currency.code === selectedCountry.currency);
      setFormData({
        ...formData,
        country: countryId,
        currency: countryCurrency ? countryCurrency._id : formData.currency
      });
    } else {
      setFormData({
        ...formData,
        country: countryId
      });
    }
  };

  const getSelectedCurrency = () => {
    return currencies.find(currency => currency._id === formData.currency);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      price: '',
      discount: 0,
      country: '',
      currency: '',
      billingCycle: 'monthly',
      storageLimitMB: '',
      maxUsers: '',
      maxLeads: '',
      maxCustomers: '',
      enabledModules: {
        leads: true,
        customers: true,
        followups: true,
        analytics: false,
        reports: false,
        integrations: false
      },
      features: [],
      isActive: true
    });
    setSelectedPlan(null);
  };

  const handleCreate = () => {
    resetForm();
    setModalMode('create');
    setShowModal(true);
  };

  const handleEdit = (plan) => {
    setSelectedPlan(plan);
    setFormData({
      name: plan.name || '',
      description: plan.description || '',
      price: plan.price || '',
      discount: plan.discount || 0,
      country: plan.country?._id || plan.country || '',
      currency: plan.currency?._id || plan.currency || '',
      billingCycle: plan.billingCycle || 'monthly',
      storageLimitMB: plan.storageLimitMB || '',
      maxUsers: plan.maxUsers || '',
      enabledModules: plan.enabledModules || {
        leads: true,
        customers: true,
        followups: true,
        analytics: false,
        reports: false,
        integrations: false
      },
      features: plan.features || [],
      isActive: plan.isActive !== undefined ? plan.isActive : true
    });
    setModalMode('edit');
    setShowModal(true);
  };

  const handleView = (plan) => {
    setSelectedPlan(plan);
    setModalMode('view');
    setShowModal(true);
  };

  const handleDelete = (plan) => {
    setPlanToDelete(plan);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!planToDelete) return;

    try {
      await plansAPI.delete(planToDelete._id);
      toast.success('Plan deleted successfully');
      fetchPlans();
      setShowDeleteModal(false);
      setPlanToDelete(null);
    } catch (error) {
      console.error('Error deleting plan:', error);
      toast.error('Failed to delete plan');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const submitData = {
        ...formData,
        price: parseFloat(formData.price),
        discount: parseInt(formData.discount) || 0,
        storageLimitMB: formData.storageLimitMB ? parseInt(formData.storageLimitMB) : null,
        maxUsers: formData.maxUsers ? parseInt(formData.maxUsers) : null,
        maxLeads: formData.maxLeads ? parseInt(formData.maxLeads) : null,
        maxCustomers: formData.maxCustomers ? parseInt(formData.maxCustomers) : null
      };

      // Remove empty country/currency fields
      if (!submitData.country || submitData.country === '') {
        delete submitData.country;
      }
      if (!submitData.currency || submitData.currency === '') {
        delete submitData.currency;
      }

      if (modalMode === 'create') {
        await plansAPI.create(submitData);
        toast.success('Plan created successfully');
      } else if (modalMode === 'edit') {
        await plansAPI.update(selectedPlan._id, submitData);
        toast.success('Plan updated successfully');
      }

      setShowModal(false);
      resetForm();
      fetchPlans();
    } catch (error) {
      console.error('Error saving plan:', error);
      toast.error(`Failed to ${modalMode} plan`);
    } finally {
      setSubmitting(false);
    }
  };

  const togglePlanStatus = async (planId) => {
    try {
      await plansAPI.toggleStatus(planId);
      toast.success('Plan status updated');
      fetchPlans();
    } catch (error) {
      console.error('Error toggling plan status:', error);
      toast.error('Failed to update plan status');
    }
  };

  const groupedPlans = plans.reduce((acc, plan) => {
    const countryKey = plan.country?._id || 'global';
    const countryName = plan.country?.name || 'Global';
    if (!acc[countryKey]) {
      acc[countryKey] = {
        country: plan.country || { name: 'Global', flag: '🌍' },
        plans: []
      };
    }
    acc[countryKey].plans.push(plan);
    return acc;
  }, {});

  const groupedPlansArray = Object.values(groupedPlans);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="large" text="Loading plans..." />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex justify-between items-center"
        >
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
              <SparklesIcon className="h-8 w-8 mr-3 text-blue-600" />
              Plans Management
            </h1>
            <p className="text-gray-600 mt-2">Create and manage subscription plans for your CRM</p>
          </div>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleCreate}
            className="inline-flex items-center px-6 py-3 border border-transparent rounded-xl shadow-lg text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Create Plan
          </motion.button>
        </motion.div>

        {/* Statistics Cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          <motion.div
            whileHover={{ y: -5 }}
            className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white shadow-lg"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm font-medium">Total Plans</p>
                <p className="text-3xl font-bold">{plans.length}</p>
              </div>
              <ClipboardDocumentListIcon className="h-8 w-8 text-blue-200" />
            </div>
          </motion.div>

          <motion.div
            whileHover={{ y: -5 }}
            className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white shadow-lg"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm font-medium">Active Plans</p>
                <p className="text-3xl font-bold">{plans.filter(plan => plan.isActive).length}</p>
              </div>
              <CheckCircleIcon className="h-8 w-8 text-green-200" />
            </div>
          </motion.div>

          <motion.div
            whileHover={{ y: -5 }}
            className="bg-gradient-to-br from-yellow-500 to-orange-500 rounded-xl p-6 text-white shadow-lg"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-yellow-100 text-sm font-medium">Avg. Price</p>
                <p className="text-3xl font-bold">
                  ${plans.length ? (plans.reduce((sum, plan) => sum + (plan.price || 0), 0) / plans.length).toFixed(0) : '0'}
                </p>
              </div>
              <CurrencyDollarIcon className="h-8 w-8 text-yellow-200" />
            </div>
          </motion.div>

          <motion.div
            whileHover={{ y: -5 }}
            className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white shadow-lg"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm font-medium">Total Subscriptions</p>
                <p className="text-3xl font-bold">
                  {plans.reduce((sum, plan) => sum + (plan.companiesCount || 0), 0)}
                </p>
              </div>
              <UsersIcon className="h-8 w-8 text-purple-200" />
            </div>
          </motion.div>
        </motion.div>

        {/* Plans Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-xl border border-white/20 overflow-hidden"
        >
          <div className="px-6 py-4 border-b border-gray-100/50">
            <h3 className="text-lg font-semibold text-gray-900">All Plans</h3>
            <p className="text-sm text-gray-600 mt-1">Manage your subscription plans</p>
          </div>

          {groupedPlansArray.length > 0 ? (
            <div className="space-y-8 p-6">
              {groupedPlansArray.map((group, groupIndex) => (
                <motion.div
                  key={group.country._id || 'global'}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: groupIndex * 0.1 }}
                  className="space-y-4"
                >
                  {/* Country Header */}
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center space-x-2 bg-gradient-to-r from-blue-50 to-indigo-50 px-4 py-2 rounded-xl border border-blue-100">
                      <span className="text-2xl">{group.country.flag}</span>
                      <h4 className="text-lg font-semibold text-gray-900">{group.country.name}</h4>
                      <span className="text-sm text-gray-500 bg-white px-2 py-1 rounded-lg">
                        {group.plans.length} plan{group.plans.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>

                  {/* Plans Grid for this Country */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                    {group.plans.map((plan, index) => (
                      <motion.div
                        key={plan._id}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.3, delay: (groupIndex * 0.1) + (index * 0.05) }}
                        className="group bg-white/90 backdrop-blur-sm rounded-xl shadow-md hover:shadow-lg border border-white/50 overflow-hidden transition-all duration-300 hover:-translate-y-1 cursor-pointer"
                        onClick={() => handleView(plan)}
                      >
                        {/* Card Header - Compact */}
                        <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-3 relative overflow-hidden">
                          <div className="absolute inset-0 bg-black/10"></div>
                          <div className="relative flex items-center justify-between">
                            <div className="flex items-center space-x-2 flex-1 min-w-0">
                              <div className="w-8 h-8 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center border border-white/30 flex-shrink-0">
                                <ClipboardDocumentListIcon className="h-4 w-4 text-white" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <h3 className="text-sm font-bold text-white truncate">{plan.name}</h3>
                                <p className="text-white/80 text-xs truncate">{plan.description}</p>
                              </div>
                            </div>
                            <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${
                              plan.isActive
                                ? 'bg-emerald-500/20 text-emerald-100 border border-emerald-400/30'
                                : 'bg-gray-500/20 text-gray-100 border border-gray-400/30'
                            }`}>
                              <div className={`w-1 h-1 rounded-full mr-1 ${
                                plan.isActive ? 'bg-emerald-400' : 'bg-gray-400'
                              }`}></div>
                              {plan.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                        </div>

                        {/* Card Content - Compact */}
                        <div className="p-3 space-y-2">
                          {/* Price */}
                          <div className="text-center">
                            <p className="text-gray-900 font-bold text-sm">
                              {plan.currency?.symbol || '$'}{plan.price?.toFixed(2) || '0.00'}
                            </p>
                            <p className="text-gray-500 text-xs">/{plan.billingCycle || 'month'}</p>
                          </div>

                          {/* Limits - Compact */}
                          <div className="flex items-center justify-between text-xs text-gray-600">
                            <div className="flex items-center">
                              <UsersIcon className="h-3 w-3 mr-1 text-blue-500" />
                              {plan.maxUsers || '∞'}
                            </div>
                            <div className="flex items-center">
                              <ServerIcon className="h-3 w-3 mr-1 text-green-500" />
                              {plan.storageLimitMB ? `${(plan.storageLimitMB / 1024).toFixed(1)}GB` : '∞'}
                            </div>
                          </div>

                          {/* Companies Count */}
                          <div className="text-center">
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {plan.companiesCount || 0} companies
                            </span>
                          </div>
                        </div>

                        {/* Card Actions - Compact */}
                        <div className="px-3 pb-3">
                          <div className="flex items-center justify-center space-x-1">
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleView(plan);
                              }}
                              className="flex items-center justify-center w-8 h-8 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors text-xs"
                              title="View Plan"
                            >
                              <EyeIcon className="h-3.5 w-3.5" />
                            </motion.button>
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEdit(plan);
                              }}
                              className="flex items-center justify-center w-8 h-8 bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 transition-colors text-xs"
                              title="Edit Plan"
                            >
                              <PencilIcon className="h-3.5 w-3.5" />
                            </motion.button>
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={(e) => {
                                e.stopPropagation();
                                togglePlanStatus(plan._id);
                              }}
                              className={`flex items-center justify-center w-8 h-8 rounded-lg transition-colors text-xs ${
                                plan.isActive
                                  ? 'bg-orange-50 text-orange-700 hover:bg-orange-100'
                                  : 'bg-green-50 text-green-700 hover:bg-green-100'
                              }`}
                              title={plan.isActive ? 'Deactivate Plan' : 'Activate Plan'}
                            >
                              {plan.isActive ? <XCircleIcon className="h-3.5 w-3.5" /> : <CheckCircleIcon className="h-3.5 w-3.5" />}
                            </motion.button>
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(plan);
                              }}
                              className="flex items-center justify-center w-8 h-8 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-colors text-xs"
                              title="Delete Plan"
                            >
                              <TrashIcon className="h-3.5 w-3.5" />
                            </motion.button>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="p-12 text-center">
              <ClipboardDocumentListIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No plans found</h3>
              <p className="mt-1 text-sm text-gray-500">Create your first plan to get started.</p>
              <div className="mt-6">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleCreate}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-xl text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <PlusIcon className="h-5 w-5 mr-2" />
                  Create Plan
                </motion.button>
              </div>
            </div>
          )}
        </motion.div>

        {/* Create/Edit Modal */}
        <AnimatePresence>
          {showModal && (modalMode === 'create' || modalMode === 'edit') && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white rounded-3xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden border border-gray-100"
              >
                <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-white/20 backdrop-blur-sm rounded-xl shadow-lg">
                        <SparklesIcon className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-white">
                          {modalMode === 'create' ? 'Create New Plan' : 'Edit Plan'}
                        </h3>
                        <p className="text-white/80 text-sm mt-1">
                          {modalMode === 'create' ? 'Set up a new subscription plan' : 'Modify plan details'}
                        </p>
                      </div>
                    </div>
                    <motion.button
                      whileHover={{ scale: 1.1, rotate: 90 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => setShowModal(false)}
                      className="p-2 hover:bg-white/20 rounded-xl transition-all duration-200 backdrop-blur-sm"
                    >
                      <XMarkIcon className="h-5 w-5 text-white" />
                    </motion.button>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-semibold text-gray-700 mb-1">
                        Plan Name *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white shadow-sm"
                        placeholder="Enter plan name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">
                        Price *
                      </label>
                      <div className="space-y-2">
                        {/* Discount Display */}
                        {formData.price && formData.discount > 0 && (
                          <div className="flex items-center justify-between bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-2">
                            <div className="flex items-center space-x-2">
                              <div className="bg-green-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                                {formData.discount}% OFF
                              </div>
                              <div className="flex items-center space-x-2">
                                <span className="text-gray-500 line-through text-sm">
                                  {getSelectedCurrency()?.symbol || '$'}{(parseFloat(formData.price) / (1 - formData.discount / 100)).toFixed(2)}
                                </span>
                                <span className="text-green-600 font-semibold">
                                  {getSelectedCurrency()?.symbol || '$'}{formData.price}
                                </span>
                              </div>
                            </div>
                            <span className="text-green-600 text-sm font-medium">
                              Save {getSelectedCurrency()?.symbol || '$'}{(parseFloat(formData.price) / (1 - formData.discount / 100) - parseFloat(formData.price)).toFixed(2)}
                            </span>
                          </div>
                        )}

                        {/* Price Input */}
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <span className="text-gray-500 font-semibold text-base">
                              {getSelectedCurrency()?.symbol || '$'}
                            </span>
                          </div>
                          <input
                            type="number"
                            step="0.01"
                            required
                            value={formData.price}
                            onChange={(e) => setFormData({...formData, price: e.target.value})}
                            className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white shadow-sm"
                            placeholder="0.00"
                          />
                          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                            <span className="text-xs text-gray-400 uppercase">
                              {getSelectedCurrency()?.code || 'USD'}
                            </span>
                          </div>
                        </div>

                        {/* Discount Percentage */}
                        <div className="relative">
                          <label className="block text-sm font-semibold text-gray-700 mb-1">
                            Discount (%)
                          </label>
                          <div className="relative">
                            <input
                              type="number"
                              min="0"
                              max="100"
                              step="1"
                              value={formData.discount}
                              onChange={(e) => setFormData({...formData, discount: parseInt(e.target.value) || 0})}
                              className="w-full pl-3 pr-10 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white shadow-sm"
                              placeholder="0"
                            />
                            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                              <span className="text-gray-500 font-semibold text-lg">%</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">
                        Billing Cycle
                      </label>
                      <select
                        value={formData.billingCycle}
                        onChange={(e) => setFormData({...formData, billingCycle: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white shadow-sm"
                      >
                        <option value="monthly">Monthly</option>
                        <option value="yearly">Yearly</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">
                        Country *
                      </label>
                      <select
                        required
                        value={formData.country}
                        onChange={(e) => handleCountryChange(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white shadow-sm"
                      >
                        <option value="">Select Country</option>
                        {countries.map((country) => (
                          <option key={country._id} value={country._id}>
                            {country.flag} {country.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">
                        Currency *
                      </label>
                      <select
                        required
                        value={formData.currency}
                        onChange={(e) => setFormData({...formData, currency: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white shadow-sm"
                      >
                        <option value="">Select Currency</option>
                        {currencies.map((currency) => (
                          <option key={currency._id} value={currency._id}>
                            {currency.symbol} {currency.name} ({currency.code})
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">
                        Max Users
                      </label>
                      <input
                        type="number"
                        value={formData.maxUsers}
                        onChange={(e) => setFormData({...formData, maxUsers: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white shadow-sm"
                        placeholder="Unlimited"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">
                        Storage Limit (MB)
                      </label>
                      <input
                        type="number"
                        value={formData.storageLimitMB}
                        onChange={(e) => setFormData({...formData, storageLimitMB: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white shadow-sm"
                        placeholder="Unlimited"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-semibold text-gray-700 mb-1">
                        Description
                      </label>
                      <textarea
                        rows={3}
                        value={formData.description}
                        onChange={(e) => setFormData({...formData, description: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white shadow-sm"
                        placeholder="Describe the plan features and benefits..."
                      />
                    </div>

                    <div className="md:col-span-2 flex items-center">
                      <input
                        type="checkbox"
                        id="isActive"
                        checked={formData.isActive}
                        onChange={(e) => setFormData({...formData, isActive: e.target.checked})}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="isActive" className="ml-3 text-sm font-medium text-gray-700">
                        Plan is active and available for subscription
                      </label>
                    </div>
                  </div>

                  <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                    <motion.button
                      type="button"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setShowModal(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-all duration-200 shadow-sm"
                    >
                      Cancel
                    </motion.button>
                    <motion.button
                      type="submit"
                      disabled={submitting}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 hover:from-blue-700 hover:via-purple-700 hover:to-indigo-700 rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
                    >
                      {submitting ? (
                        <div className="flex items-center">
                          <LoadingSpinner size="small" />
                          <span className="ml-2">
                            {modalMode === 'create' ? 'Creating...' : 'Updating...'}
                          </span>
                        </div>
                      ) : (
                        modalMode === 'create' ? 'Create Plan' : 'Update Plan'
                      )}
                    </motion.button>
                  </div>
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* View Modal */}
        <AnimatePresence>
          {showModal && modalMode === 'view' && selectedPlan && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              >
                <div className="px-8 py-6 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center mr-4">
                        <ClipboardDocumentListIcon className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-gray-900">{selectedPlan.name}</h3>
                        <p className="text-gray-600">Plan Details</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowModal(false)}
                      className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                    >
                      <XMarkIcon className="h-6 w-6 text-gray-400" />
                    </button>
                  </div>
                </div>

                <div className="px-8 py-6 space-y-6">
                  <div className="grid grid-cols-2 gap-6">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="text-sm text-gray-500 mb-1">Price</div>
                      <div className="text-2xl font-bold text-gray-900">
                        {selectedPlan.currency?.symbol || '$'}{selectedPlan.price?.toFixed(2)}
                        <span className="text-sm font-normal text-gray-500">/{selectedPlan.billingCycle}</span>
                      </div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="text-sm text-gray-500 mb-1">Status</div>
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                        selectedPlan.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {selectedPlan.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                      <UsersIcon className="h-8 w-8 text-blue-600 mx-auto mb-2" />
                      <div className="text-2xl font-bold text-gray-900">{selectedPlan.maxUsers || '∞'}</div>
                      <div className="text-sm text-gray-600">Max Users</div>
                    </div>
                    <div className="text-center p-4 bg-green-50 rounded-lg">
                      <ServerIcon className="h-8 w-8 text-green-600 mx-auto mb-2" />
                      <div className="text-2xl font-bold text-gray-900">
                        {selectedPlan.storageLimitMB ? `${(selectedPlan.storageLimitMB / 1024).toFixed(1)}GB` : '∞'}
                      </div>
                      <div className="text-sm text-gray-600">Storage</div>
                    </div>
                    <div className="text-center p-4 bg-purple-50 rounded-lg">
                      <ChartBarIcon className="h-8 w-8 text-purple-600 mx-auto mb-2" />
                      <div className="text-2xl font-bold text-gray-900">{selectedPlan.companiesCount || 0}</div>
                      <div className="text-sm text-gray-600">Companies</div>
                    </div>
                  </div>

                  {selectedPlan.description && (
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-3">Description</h4>
                      <p className="text-gray-700 bg-gray-50 rounded-lg p-4">{selectedPlan.description}</p>
                    </div>
                  )}

                  <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                    <button
                      onClick={() => {
                        setModalMode('edit');
                        setFormData({
                          name: selectedPlan.name || '',
                          description: selectedPlan.description || '',
                          price: selectedPlan.price || '',
                          country: selectedPlan.country?._id || selectedPlan.country || '',
                          currency: selectedPlan.currency?._id || selectedPlan.currency || '',
                          billingCycle: selectedPlan.billingCycle || 'monthly',
                          storageLimitMB: selectedPlan.storageLimitMB || '',
                          maxUsers: selectedPlan.maxUsers || '',
                          enabledModules: selectedPlan.enabledModules || {
                            leads: true,
                            customers: true,
                            followups: true,
                            analytics: false,
                            reports: false,
                            integrations: false
                          },
                          features: selectedPlan.features || [],
                          isActive: selectedPlan.isActive !== undefined ? selectedPlan.isActive : true
                        });
                      }}
                      className="px-6 py-3 text-sm font-medium text-blue-700 bg-blue-100 hover:bg-blue-200 rounded-lg transition-colors"
                    >
                      Edit Plan
                    </button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Delete Confirmation Modal */}
        <AnimatePresence>
          {showDeleteModal && planToDelete && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white rounded-2xl shadow-2xl max-w-md w-full"
              >
                <div className="px-6 py-6">
                  <div className="flex items-center justify-center mb-4">
                    <div className="h-16 w-16 rounded-full bg-red-100 flex items-center justify-center">
                      <ExclamationTriangleIcon className="h-8 w-8 text-red-600" />
                    </div>
                  </div>

                  <div className="text-center">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Delete Plan</h3>
                    <p className="text-gray-600 mb-4">
                      Are you sure you want to delete <strong>{planToDelete.name}</strong>?
                    </p>

                    {planToDelete.companiesCount > 0 && (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                        <div className="flex">
                          <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400 mr-3 flex-shrink-0 mt-0.5" />
                          <div className="text-sm text-yellow-800">
                            <p className="font-medium">Warning!</p>
                            <p>This plan is currently used by {planToDelete.companiesCount} companies. Deleting it may affect their subscriptions.</p>
                          </div>
                        </div>
                      </div>
                    )}

                    <p className="text-sm text-gray-500">This action cannot be undone.</p>
                  </div>
                </div>

                <div className="px-6 py-4 bg-gray-50 flex space-x-3 rounded-b-2xl">
                  <button
                    type="button"
                    onClick={() => setShowDeleteModal(false)}
                    className="flex-1 px-4 py-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={confirmDelete}
                    className="flex-1 px-4 py-3 text-sm font-medium text-white bg-red-600 border border-transparent rounded-lg hover:bg-red-700 transition-colors"
                  >
                    Delete Plan
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </Layout>
  );
};

export default Plans;